import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { Schemas } from '@/api/client';
import { qk } from '@/api/queries';
import type { Organization } from '@/api/types';
import { Button, Chip, Drawer, Input, Segmented, Textarea, useToast } from '@/design-system';
import { errorField, errorText } from '@/shell/errorText';
import { updateOrganization } from './api';
import s from './organizations.module.css';

const HEX = /^#[0-9a-fA-F]{6}$/;
type Status = Organization['status'];

/** Only the fields that changed, with blanks as null (the API clears a field with null). */
function diff(org: Organization, next: Record<string, unknown>): Schemas['OrganizationPatch'] {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(next)) {
    const before = (org as Record<string, unknown>)[k];
    if (JSON.stringify(before ?? null) !== JSON.stringify(v ?? null)) out[k] = v;
  }
  return out as Schemas['OrganizationPatch'];
}

/**
 * «Изменить» on the profile (DESIGN_SPEC §9 C8, additive): every TZ §5 field of the organization in the
 * design's drawer — brand, legal name and form, TIN, industry, description and goals, contacts, address,
 * site languages, brand colours, status, notes. PATCH /admin/organizations/{id} with the changed fields.
 */
export function EditProfileDrawer({ org, onClose }: { org: Organization; onClose: () => void }) {
  const { t } = useTranslation('organizations');
  const { t: tc } = useTranslation('common');
  const toast = useToast();
  const qc = useQueryClient();
  const [f, setF] = useState({
    brand_name: org.brand_name,
    legal_name: org.legal_name ?? '',
    legal_type: org.legal_type ?? '',
    tin: org.tin ?? '',
    industry: org.industry ?? '',
    description: org.description ?? '',
    goals: org.goals ?? '',
    phone: org.phone ?? '',
    email: org.email ?? '',
    address: org.address ?? '',
    notes: org.notes ?? '',
  });
  const [locales, setLocales] = useState<string[]>(org.locales);
  const [colorMode, setColorMode] = useState<Organization['color_mode']>(org.color_mode);
  const [colors, setColors] = useState(org.brand_colors.map((c) => c.toUpperCase()).join(' '));
  const [status, setStatus] = useState<Status>(org.status);
  const [problem, setProblem] = useState<{ field: string | null; text: string } | null>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: (body: Schemas['OrganizationPatch']) => updateOrganization(org.id, body),
    onSuccess: (next) => {
      qc.setQueryData(qk.organization(org.id), next);
      void qc.invalidateQueries({ queryKey: ['organizations'] });
      void qc.invalidateQueries({ queryKey: qk.orgActivity(org.id) });
      toast(t('edit.saved'));
      onClose();
    },
    onError: (err) => setProblem({ field: errorField(err), text: errorText(err, tc) }),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const list = colors.split(/[\s,;]+/).filter(Boolean);
    if (!f.brand_name.trim()) return setProblem({ field: 'brand_name', text: t('new.brand_required') });
    if (!locales.length) return setProblem({ field: 'locales', text: t('edit.locales_required') });
    if (list.some((c) => !HEX.test(c)) || list.length > 8) return setProblem({ field: 'brand_colors', text: t('edit.colors_invalid') });
    const blank = (v: string) => v.trim() || null;
    const body = diff(org, {
      brand_name: f.brand_name.trim(),
      legal_name: blank(f.legal_name),
      legal_type: blank(f.legal_type),
      tin: blank(f.tin.replace(/\s/g, '')),
      industry: blank(f.industry),
      description: blank(f.description),
      goals: blank(f.goals),
      phone: blank(f.phone),
      email: blank(f.email),
      address: blank(f.address),
      notes: blank(f.notes),
      locales: ['ru', 'uz'].filter((l) => locales.includes(l)),
      color_mode: colorMode,
      brand_colors: list.map((c) => c.toLowerCase()),
      status,
    });
    setProblem(null);
    if (!Object.keys(body).length) return onClose();
    save.mutate(body);
  };

  const invalid = (field: string) => (problem?.field === field ? true : undefined);
  return (
    <Drawer
      open
      onClose={onClose}
      title={t('edit.title')}
      sub={t('edit.sub')}
      closeLabel={tc('actions.close_esc')}
      footerNote={
        problem && (
          <span className={s.problem} role="alert">
            {problem.text}
          </span>
        )
      }
      footer={
        <div className={s.drawerActions}>
          <Button size={38} variant="secondary" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button size={38} type="submit" form="org-profile" disabled={save.isPending}>
            {t('edit.save')}
          </Button>
        </div>
      }
    >
      <form id="org-profile" className={s.drawerForm} onSubmit={submit} noValidate>
        <div className={s.grid2}>
          <Input label={t('edit.brand')} value={f.brand_name} onChange={set('brand_name')} aria-invalid={invalid('brand_name')} required />
          <Input label={t('edit.industry')} value={f.industry} onChange={set('industry')} />
        </div>
        <Input label={t('edit.legal')} value={f.legal_name} onChange={set('legal_name')} aria-invalid={invalid('legal_name')} />
        <div className={s.grid2}>
          <Input label={t('edit.form')} value={f.legal_type} onChange={set('legal_type')} />
          <Input label={t('edit.tin')} value={f.tin} onChange={set('tin')} inputMode="numeric" mono aria-invalid={invalid('tin')} />
        </div>
        <Textarea label={t('edit.description')} rows={3} value={f.description} onChange={set('description')} />
        <Textarea label={t('edit.goals')} rows={2} value={f.goals} onChange={set('goals')} />
        <div className={s.grid2}>
          <Input label={t('edit.phone')} type="tel" value={f.phone} onChange={set('phone')} aria-invalid={invalid('phone')} />
          <Input label={t('edit.email')} type="email" value={f.email} onChange={set('email')} aria-invalid={invalid('email')} />
        </div>
        <Input label={t('edit.address')} value={f.address} onChange={set('address')} />
        <div className={s.group}>
          <span className={s.groupLabel}>{t('edit.locales')}</span>
          <div className={s.chips}>
            {['ru', 'uz'].map((l) => (
              <Chip key={l} variant="light" selected={locales.includes(l)} onClick={() => setLocales((x) => (x.includes(l) ? x.filter((y) => y !== l) : [...x, l]))}>
                {l.toUpperCase()}
              </Chip>
            ))}
          </div>
        </div>
        <div className={s.group}>
          <span className={s.groupLabel}>{t('edit.colors')}</span>
          <Segmented
            variant="form"
            label={t('edit.colors')}
            value={colorMode}
            onChange={setColorMode}
            className={s.alignStart}
            options={(['ai', 'manual'] as const).map((k) => ({ value: k, label: t(`edit.color_mode.${k}`) }))}
          />
          <Input aria-label={t('edit.colors')} value={colors} onChange={(e) => setColors(e.target.value)} placeholder={t('edit.colors_hint')} mono aria-invalid={invalid('brand_colors')} />
        </div>
        <div className={s.group}>
          <span className={s.groupLabel}>{t('edit.status')}</span>
          <Segmented
            variant="form"
            label={t('edit.status')}
            value={status}
            onChange={setStatus}
            className={s.alignStart}
            options={(['active', 'trial', 'archived'] as const).map((k) => ({ value: k, label: t(`edit.statuses.${k}`) }))}
          />
        </div>
        <Textarea label={t('edit.notes')} rows={3} value={f.notes} onChange={set('notes')} />
      </form>
    </Drawer>
  );
}
